import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { PERMISSION_NAMES } from "../../../constants/permissions";
import { BaseQueryErrorHandler } from "./@utils/BaseQueryErrorHandler";

const rawBaseQuery = fetchBaseQuery({
  baseUrl: "/api/v2/jwt-auth",
  prepareHeaders: (headers) => {
    headers.set("Content-Type", "application/json");

    return headers;
  },
});

const baseQuery = BaseQueryErrorHandler(rawBaseQuery);

export const authApi = createApi({
  reducerPath: "authApi",
  baseQuery,
  endpoints: (builder) => ({
    getAuthStatus: builder.query({
      query: () => "/auth",
      transformResponse: (response) => {
        let pages = Object.keys(response.permissions);

        pages = pages.map((page) =>
          page === PERMISSION_NAMES.uiremoteDesktop ||
          page === PERMISSION_NAMES.uiremoteMobile
            ? PERMISSION_NAMES.uiremoteConcat
            : page
        );

        pages = [...new Set(pages)];

        return {
          ...response,
          pages,
        };
      },
    }),
    postRefresh: builder.mutation({
      query: () => ({
        url: "/refresh",
        method: "POST",
      }),
    }),
    getAllUsers: builder.query({
      query: () => "/users",
    }),
    getAllRoles: builder.query({
      query: () => "/roles",
    }),
    getSpecificRole: builder.query({
      query: ({ roleName }) => `/role/${roleName}`,
    }),
    getAllPermissions: builder.query({
      query: () => "/permissions",
    }),
    postLogin: builder.mutation({
      query: ({ email, password }) => ({
        url: "/login",
        method: "POST",
        body: { email, password },
      }),
    }),
    postLogout: builder.mutation({
      query: () => ({
        url: "/logout",
        method: "POST",
        body: {},
      }),
    }),
    putUpdateUser: builder.mutation({
      query: ({
        userEmail,
        roles,
        permissions,
        first_name,
        last_name,
        full_name,
      }) => ({
        url: `/user/${userEmail}`,
        method: "PUT",
        body: { roles, permissions, first_name, last_name, full_name },
      }),
    }),
    postNewUser: builder.mutation({
      query: ({
        userEmail,
        roles,
        permissions,
        first_name,
        last_name,
        password,
      }) => {
        const body = {
          roles,
          permissions,
        };

        if (first_name?.trim()) body.first_name = first_name;
        if (last_name?.trim()) body.last_name = last_name;

        const fullName = [first_name, last_name]
          .filter(Boolean)
          .join(" ")
          .trim();
        if (fullName) body.full_name = fullName;

        if (password?.trim()) body.password = password;

        return {
          url: `/user/${userEmail}`,
          method: "POST",
          body,
        };
      },
    }),
    postNewRole: builder.mutation({
      query: ({ roleName, description, permissions }) => ({
        url: `/role/${roleName}`,
        method: "POST",
        body: { description, permissions },
      }),
    }),
  }),
});

export const {
  useGetAuthStatusQuery,
  usePostRefreshMutation,
  useGetAllUsersQuery,
  useGetAllRolesQuery,
  useGetSpecificRoleQuery,
  useGetAllPermissionsQuery,
  usePostLoginMutation,
  usePostLogoutMutation,
  usePutUpdateUserMutation,
  usePostNewUserMutation,
  usePostNewRoleMutation,
} = authApi;
